
import { Component, Prop, Vue } from "vue-property-decorator";
import { SMUser, SMCases } from "@/store";

import { TCase_Applicant } from "@/models/cases_expert";
import { API_ROUTES } from "@/utils/API";
import { API_TReport } from "@/models/report";

const FILE_NAME_EXT = ".pdf";

const REPORT_DOWNLOAD_TYPE = "pdf";

const EDITOR_PARAMS = {
  outputType: "html",
  dataControls: false,
};

@Component
export default class CoverLetter extends Vue {
  @Prop({ type: Object }) readonly src?: API_TReport;
  @Prop({ type: Number }) readonly serviceId?: number;
  @Prop({ type: String }) readonly alias?: string;
  @Prop({ type: Boolean, default: false }) readonly isEditable: boolean | undefined;
  @Prop({ type: Boolean, default: false }) readonly loading: boolean | undefined;
  @Prop({ type: Boolean, default: false }) readonly isHOEmail: boolean;

  editing = false;

  editorParams = EDITOR_PARAMS;

  get showPreview() {
    return !this.editing && (this.src as API_TReport).content;
  }

  get user() {
    return SMUser.user;
  }

  convertString(s: string): string {
    return s.charAt(0).toUpperCase() + s.slice(1).replaceAll("_", " ");
  }

  get fileName(): string {
    const { query, params } = this.$route;

    const name = (query?.name || params?.name || "") as string;

    return this.convertString(name);
  }

  get currentService(): any | { main_applicant: TCase_Applicant } {
    return SMCases.case;
  }

  get mainApplicant(): TCase_Applicant {
    return this.currentService?.main_applicant;
  }

  async edit() {
    this.editing = true;
  }

  cancel() {
    this.editing = false;
  }

  convertHtmlToBlob(data: string) {
    const formData = new FormData();

    formData.append("content", data);

    return formData;
  }

  submitSave() {
    const editor = this.$refs.editor as Vue & { save: () => {} };

    editor.save && editor.save();
  }

  async save(payload: { html: string }) {
    try {
      this.$emit("update-content", payload.html);

      // TODO: check emits: ['update:loading']
      // this.$emit("update:loading", true);

      await this.$http.put(
        API_ROUTES.CASE_REPORT_EDIT(String(this.serviceId), this.alias),
        this.convertHtmlToBlob(payload.html)
      );
    } finally {
      // this.$emit("update:loading", false);

      this.cancel();
    }
  }

  generatePdfName() {
    const { serviceId, mainApplicant } = this;

    const name = `${mainApplicant?.first_name} ${mainApplicant?.last_name}`;

    return `${this.fileName} ${serviceId} - ${name}${FILE_NAME_EXT}`;
  }

  async getFileAndDownload() {
    const { serviceId } = this;

    const { data } = await this.$http.get(
      API_ROUTES.CASE_REPORT_PARSE(String(serviceId), this.alias, REPORT_DOWNLOAD_TYPE),
      {
        responseType: "arraybuffer",
      }
    );

    this.download(data, this.generatePdfName(), "application/pdf");
  }

  download(data: ArrayBuffer, filename: string, type: string) {
    const blob = new Blob([data], { type });

    if (window.navigator.msSaveOrOpenBlob) return window.navigator.msSaveOrOpenBlob(blob, filename);

    const a = document.createElement("a");
    const url = URL.createObjectURL(blob);

    a.href = url;
    a.download = filename;

    document.body.appendChild(a);

    a.click();

    setTimeout(() => {
      document.body.removeChild(a);

      window.URL.revokeObjectURL(url);
    }, 0);
  }

  regenerateLoading = false;

  async regenerate(isHOEmail: boolean) {
    if (!this.serviceId) return;

    const url = isHOEmail
      ? API_ROUTES.CASE_REPORT_GENERATE(this.serviceId, this.alias)
      : API_ROUTES.CASES_REPORT_REGENERATE(this.serviceId, this.alias);

    try {
      this.regenerateLoading = true;

      await this.axios.get(url);

      this.$emit("update");
    } finally {
      this.cancel();

      this.regenerateLoading = false;
    }
  }

  generateLoading = false;

  async createDraft() {
    this.generateLoading = true;

    const { serviceId } = this;

    if (!serviceId) return;

    try {
      const url = API_ROUTES.CASES_REPORT_DRAFT(serviceId, this.alias);

      await this.axios.get(url);
    } finally {
      this.generateLoading = false;
    }
  }
}
